import { FilterList, InboxOutlined, KeyboardArrowDown } from 'components/icons';
import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { cn } from 'utils/cn';
import { NEUTRAL_TILE_CLASS } from 'utils/families';
import { t } from 'utils/getTranslations';

import { InboxCategory, InboxCategoryId } from './inboxFeed';

export type CategoryFilter = InboxCategoryId | 'all';

export interface CategoryOption {
    category: InboxCategory;
    /** What the menu prints at the row's end: "3 unread", "2 open", or nothing. */
    countLabel: string;
}

const TILE_CLASS = 'flex size-[22px] flex-none items-center justify-center rounded-md';

/**
 * The Inbox's one filter: a button naming the category shown, which opens the menu of every category
 * with its count. The categories sit here rather than in a tab strip, so the feed keeps the drawer's width.
 */
export function InboxCategoryFilter({
    value,
    options,
    allCountLabel,
    onChange,
}: {
    value: CategoryFilter;
    options: CategoryOption[];
    allCountLabel: string;
    onChange: (next: CategoryFilter) => void;
}) {
    const selected = options.find(({ category }) => category.id === value)?.category;
    const TriggerIcon = selected?.icon ?? FilterList;
    const name = t('notifications.feed.category');

    const rows = [
        {
            id: 'all' as const,
            label: t('notifications.feed.all-categories'),
            icon: InboxOutlined,
            tileClassName: NEUTRAL_TILE_CLASS,
            countLabel: allCountLabel,
        },
        ...options.map(({ category, countLabel }) => ({
            id: category.id,
            label: t(category.labelId),
            icon: category.icon,
            tileClassName: category.tileClassName,
            countLabel,
        })),
    ];

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <Button
                        variant="outline"
                        size="sm"
                        aria-label={t('notifications.feed.category-label', {
                            category: selected ? t(selected.labelId) : t('notifications.feed.all-categories'),
                        })}
                        className="border-border-control text-meta hover:border-grey-400 hover:bg-card aria-expanded:bg-card h-8 gap-1.5 rounded-lg ps-2.5 pe-2 font-medium shadow-none"
                    />
                }
            >
                <TriggerIcon className="size-4" />
                <span>{selected ? t(selected.labelId) : t('notifications.feed.all-categories')}</span>
                <KeyboardArrowDown className="text-muted-foreground size-[18px]" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[260px]">
                <DropdownMenuRadioGroup
                    aria-label={name}
                    value={value}
                    onValueChange={(next: CategoryFilter) => onChange(next)}
                >
                    {rows.map(({ id, label, icon: Icon, tileClassName, countLabel }) => (
                        <DropdownMenuRadioItem
                            key={id}
                            value={id}
                            closeOnClick
                            className="data-checked:bg-primary-wash text-meta h-[34px] gap-2.5 px-2 data-checked:font-semibold [&_[data-slot=dropdown-menu-radio-item-indicator]]:hidden"
                        >
                            <span className={cn(TILE_CLASS, tileClassName)}>
                                <Icon className="size-3.5" />
                            </span>
                            <span className="flex-1">{label}</span>
                            <span className="text-muted-foreground text-caption font-normal tabular-nums">
                                {countLabel}
                            </span>
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
